import { Loader2, X } from 'lucide-react';
import {
  createContext,
  forwardRef,
  useContext,
  useEffect,
  useId,
  useRef,
  type ButtonHTMLAttributes,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from 'react';
import { cn } from '../lib/cn';

/* --------------------------------- Button -------------------------------- */

type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'success' | 'outline';
type ButtonSize = 'xs' | 'sm' | 'md' | 'lg';

const BUTTON_VARIANTS: Record<ButtonVariant, string> = {
  primary: 'bg-brand-500 text-white hover:bg-brand-600 shadow-sm shadow-brand-500/25',
  secondary: 'bg-ink-750 text-ink-100 hover:bg-ink-700 border border-ink-700',
  ghost: 'text-ink-300 hover:text-ink-100 hover:bg-ink-800',
  danger: 'bg-rose-600 text-white hover:bg-rose-500',
  success: 'bg-emerald-600 text-white hover:bg-emerald-500',
  outline: 'border border-ink-700 text-ink-200 hover:border-ink-600 hover:bg-ink-800',
};

const BUTTON_SIZES: Record<ButtonSize, string> = {
  xs: 'h-7 px-2 text-xs gap-1.5 rounded-md',
  sm: 'h-8 px-3 text-xs gap-1.5 rounded-md',
  md: 'h-9 px-3.5 text-sm gap-2 rounded-lg',
  lg: 'h-11 px-5 text-sm gap-2 rounded-lg',
};

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  icon?: ReactNode;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'secondary', size = 'md', loading, icon, className, children, disabled, ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      disabled={disabled || loading}
      className={cn(
        'inline-flex items-center justify-center font-medium whitespace-nowrap transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/60 focus-visible:ring-offset-1 focus-visible:ring-offset-ink-950',
        'disabled:cursor-not-allowed disabled:opacity-50',
        BUTTON_VARIANTS[variant],
        BUTTON_SIZES[size],
        className,
      )}
      {...rest}
    >
      {loading ? <Loader2 className="size-4 animate-spin" /> : icon}
      {children}
    </button>
  );
});

export const IconButton = forwardRef<HTMLButtonElement, ButtonProps & { label: string }>(
  function IconButton({ label, className, size = 'sm', children, ...rest }, ref) {
    return (
      <Button
        ref={ref}
        aria-label={label}
        title={label}
        size={size}
        className={cn('!px-0', size === 'xs' ? 'w-7' : size === 'sm' ? 'w-8' : 'w-9', className)}
        {...rest}
      >
        {children}
      </Button>
    );
  },
);

/* --------------------------------- Inputs -------------------------------- */

// `min-w-0` matters: a form control's default `min-width: auto` resolves to its
// intrinsic size, so inside a flex or grid row it refuses to shrink and pushes
// the whole panel into horizontal overflow.
const FIELD_BASE =
  'w-full min-w-0 rounded-lg border border-ink-700 bg-ink-900 px-3 text-sm text-ink-100 placeholder:text-ink-500 ' +
  'transition-colors focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 ' +
  'disabled:cursor-not-allowed disabled:bg-ink-850 disabled:text-ink-400';

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(function Input(
  { className, ...rest },
  ref,
) {
  return <input ref={ref} className={cn(FIELD_BASE, 'h-9', className)} {...rest} />;
});

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaHTMLAttributes<HTMLTextAreaElement>>(
  function Textarea({ className, ...rest }, ref) {
    return <textarea ref={ref} className={cn(FIELD_BASE, 'py-2 leading-relaxed', className)} {...rest} />;
  },
);

export const Select = forwardRef<HTMLSelectElement, SelectHTMLAttributes<HTMLSelectElement>>(function Select(
  { className, children, ...rest },
  ref,
) {
  return (
    <select ref={ref} className={cn(FIELD_BASE, 'h-9 cursor-pointer pr-8', className)} {...rest}>
      {children}
    </select>
  );
});

export function Field({
  label,
  hint,
  error,
  required,
  children,
  className,
}: {
  label?: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  required?: boolean;
  children: ReactNode;
  className?: string;
}) {
  return (
    <label className={cn('block', className)}>
      {label && (
        <span className="mb-1.5 flex items-center gap-1 text-xs font-medium text-ink-300">
          {label}
          {required && <span className="text-rose-400">*</span>}
        </span>
      )}
      {children}
      {error ? (
        <span className="mt-1 block text-xs text-rose-400">{error}</span>
      ) : hint ? (
        <span className="mt-1 block text-xs text-ink-500">{hint}</span>
      ) : null}
    </label>
  );
}

export function Switch({
  checked,
  onChange,
  label,
  disabled,
}: {
  checked: boolean;
  onChange: (next: boolean) => void;
  label?: ReactNode;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={cn(
        'inline-flex items-center gap-2.5 text-sm transition-opacity disabled:cursor-not-allowed disabled:opacity-50',
      )}
    >
      <span
        className={cn(
          'relative h-5 w-9 shrink-0 rounded-full transition-colors',
          checked ? 'bg-brand-500' : 'bg-ink-700',
        )}
      >
        {/*
          The thumb is anchored with an explicit `left` rather than relying on
          its static position: an absolutely positioned child with `left: auto`
          resolves to wherever normal flow would have put it, which differs
          between browsers and left the thumb outside the track.
          Track 36px − thumb 16px − 2px inset each side ⇒ travel of 16px.
        */}
        <span
          className={cn(
            'absolute top-0.5 left-0.5 size-4 rounded-full bg-white shadow-sm transition-transform duration-150',
            checked ? 'translate-x-4' : 'translate-x-0',
          )}
        />
      </span>
      {label && <span className="text-ink-200">{label}</span>}
    </button>
  );
}

/* -------------------------------- Surfaces ------------------------------- */

export function Card({
  className,
  children,
  ...rest
}: { className?: string; children: ReactNode } & React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={cn('rounded-xl border border-ink-800 bg-ink-900', className)} {...rest}>
      {children}
    </div>
  );
}

export function CardHeader({
  title,
  description,
  actions,
  className,
}: {
  title: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn('flex flex-wrap sm:flex-nowrap items-start justify-between gap-3 border-b border-ink-800 px-4 sm:px-5 py-3 sm:py-4', className)}>
      <div className="min-w-0 flex-1">
        <h3 className="truncate text-sm font-semibold text-ink-100">{title}</h3>
        {description && <p className="mt-0.5 text-xs text-ink-400">{description}</p>}
      </div>
      {actions && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
    </div>
  );
}

const BADGE_TONES = {
  neutral: 'bg-ink-750 text-ink-300 border-ink-700',
  brand: 'bg-brand-500/12 text-brand-400 border-brand-500/25',
  success: 'bg-emerald-500/12 text-emerald-400 border-emerald-500/25',
  warning: 'bg-amber-500/12 text-amber-400 border-amber-500/25',
  danger: 'bg-rose-500/12 text-rose-400 border-rose-500/25',
  info: 'bg-sky-500/12 text-sky-400 border-sky-500/25',
} as const;

export function Badge({
  tone = 'neutral',
  className,
  children,
}: {
  tone?: keyof typeof BADGE_TONES;
  className?: string;
  children: ReactNode;
}) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-md border px-1.5 py-0.5 text-[11px] font-medium',
        BADGE_TONES[tone],
        className,
      )}
    >
      {children}
    </span>
  );
}

const METHOD_TONES: Record<string, keyof typeof BADGE_TONES> = {
  GET: 'success',
  POST: 'brand',
  PUT: 'warning',
  PATCH: 'warning',
  DELETE: 'danger',
};

export function MethodBadge({ method, className }: { method: string; className?: string }) {
  return (
    <Badge tone={METHOD_TONES[method] ?? 'neutral'} className={cn('font-mono tracking-tight', className)}>
      {method}
    </Badge>
  );
}

export function Spinner({ className }: { className?: string }) {
  return <Loader2 className={cn('size-4 animate-spin text-ink-400', className)} />;
}

export function EmptyState({
  icon,
  title,
  description,
  action,
  className,
}: {
  icon?: ReactNode;
  title: string;
  description?: ReactNode;
  action?: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn('flex flex-col items-center justify-center px-6 py-14 text-center', className)}>
      {icon && (
        <div className="mb-4 flex size-11 items-center justify-center rounded-xl border border-ink-800 bg-ink-850 text-ink-400">
          {icon}
        </div>
      )}
      <h3 className="text-sm font-semibold text-ink-100">{title}</h3>
      {description && <p className="mt-1.5 max-w-md text-xs leading-relaxed text-ink-400">{description}</p>}
      {action && <div className="mt-5">{action}</div>}
    </div>
  );
}

export function Alert({
  tone = 'info',
  title,
  children,
  className,
}: {
  tone?: 'info' | 'warning' | 'danger' | 'success';
  title?: ReactNode;
  children?: ReactNode;
  className?: string;
}) {
  const tones = {
    info: 'border-sky-500/25 bg-sky-500/8 text-sky-200',
    warning: 'border-amber-500/25 bg-amber-500/8 text-amber-200',
    danger: 'border-rose-500/25 bg-rose-500/8 text-rose-200',
    success: 'border-emerald-500/25 bg-emerald-500/8 text-emerald-200',
  };
  return (
    <div
      // Alerts appear in response to an action — a failed sign-in, a rejected
      // save — so a screen reader should announce them without being moved here.
      role={tone === 'danger' || tone === 'warning' ? 'alert' : 'status'}
      className={cn('rounded-lg border px-3.5 py-2.5 text-xs leading-relaxed', tones[tone], className)}
    >
      {title && <div className="mb-0.5 font-semibold">{title}</div>}
      {children}
    </div>
  );
}

export function Avatar({
  name,
  color,
  size = 28,
  className,
  title,
}: {
  name: string;
  color?: string;
  size?: number;
  className?: string;
  title?: string;
}) {
  const initials = name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('');
  return (
    <span
      title={title ?? name}
      className={cn('inline-flex shrink-0 items-center justify-center rounded-full font-semibold text-white', className)}
      style={{ width: size, height: size, background: color ?? '#6366f1', fontSize: size * 0.4 }}
    >
      {initials || '?'}
    </span>
  );
}

export function Skeleton({ className }: { className?: string }) {
  return <div className={cn('animate-pulse rounded-md bg-ink-800', className)} />;
}

/* --------------------------------- Modal --------------------------------- */

export function Modal({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  width = 'md',
}: {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  description?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  width?: 'sm' | 'md' | 'lg' | 'xl';
}) {
  const ref = useRef<HTMLDivElement>(null);
  const titleId = useId();

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    // Move focus into the dialog so keyboard users are not stranded behind it.
    const timer = window.setTimeout(() => ref.current?.focus(), 0);
    return () => {
      document.removeEventListener('keydown', onKey);
      window.clearTimeout(timer);
    };
  }, [open, onClose]);

  if (!open) return null;

  const widths = { sm: 'max-w-sm', md: 'max-w-lg', lg: 'max-w-2xl', xl: 'max-w-4xl' };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/65 p-2.5 sm:p-6 md:p-8 backdrop-blur-sm">
      <div className="absolute inset-0" onClick={onClose} aria-hidden />
      <div
        ref={ref}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className={cn(
          'forge-fade-in relative z-10 my-auto flex max-h-[92vh] w-full flex-col rounded-xl sm:rounded-2xl border border-ink-750 bg-ink-900 shadow-2xl outline-none',
          widths[width],
        )}
      >
        <div className="flex shrink-0 items-start justify-between gap-3 border-b border-ink-800 px-4 sm:px-5 py-3 sm:py-4">
          <div className="min-w-0 flex-1">
            <h2 id={titleId} className="text-sm font-semibold text-ink-100">
               {title}
            </h2>
            {description && <p className="mt-1 text-xs leading-relaxed text-ink-400">{description}</p>}
          </div>
          <IconButton label="Close" variant="ghost" onClick={onClose}>
            <X className="size-4" />
          </IconButton>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto px-4 sm:px-5 py-3 sm:py-4">{children}</div>
        {footer && (
          <div className="flex shrink-0 flex-wrap items-center justify-end gap-2 border-t border-ink-800 px-4 sm:px-5 py-3 sm:py-3.5">{footer}</div>
        )}
      </div>
    </div>
  );
}

/* --------------------------------- Tabs ---------------------------------- */

interface TabsContextValue {
  value: string;
  onChange: (next: string) => void;
}
const TabsContext = createContext<TabsContextValue | null>(null);

export function Tabs({
  value,
  onChange,
  children,
  className,
}: {
  value: string;
  onChange: (next: string) => void;
  children: ReactNode;
  className?: string;
}) {
  return (
    <TabsContext.Provider value={{ value, onChange }}>
      <div className={className}>{children}</div>
    </TabsContext.Provider>
  );
}

export function TabList({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div className={cn('flex items-center gap-1 border-b border-ink-800 overflow-x-auto scrollbar-none', className)} role="tablist">
      {children}
    </div>
  );
}

export function Tab({ id, children, count }: { id: string; children: ReactNode; count?: number }) {
  const ctx = useContext(TabsContext);
  if (!ctx) throw new Error('<Tab> must be inside <Tabs>');
  const active = ctx.value === id;
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={() => ctx.onChange(id)}
      className={cn(
        'relative -mb-px flex shrink-0 items-center gap-1.5 border-b-2 px-3 py-2 text-xs font-medium transition-colors whitespace-nowrap',
        active
          ? 'border-brand-500 text-ink-100'
          : 'border-transparent text-ink-400 hover:border-ink-700 hover:text-ink-200',
      )}
    >
      {children}
      {count !== undefined && (
        <span className="rounded bg-ink-800 px-1 py-px text-[10px] tabular-nums text-ink-400">{count}</span>
      )}
    </button>
  );
}

export function TabPanel({ id, children, className }: { id: string; children: ReactNode; className?: string }) {
  const ctx = useContext(TabsContext);
  if (!ctx || ctx.value !== id) return null;
  return (
    <div role="tabpanel" className={cn('forge-fade-in', className)}>
      {children}
    </div>
  );
}

/* ------------------------------- Utilities ------------------------------- */

export function Kbd({ children }: { children: ReactNode }) {
  return (
    <kbd className="rounded border border-ink-700 bg-ink-850 px-1.5 py-0.5 font-mono text-[10px] text-ink-400">
      {children}
    </kbd>
  );
}

export function Divider({ className }: { className?: string }) {
  return <div className={cn('h-px bg-ink-800', className)} />;
}
